import * as React from "react";
import { Badge, Checkbox } from "@/components/ui";
import { BuilderState, ClientFieldKey, FormRouteBuilder } from "../useFormRouteBuilder";
import { Helper, RouteCard, Segmented, STEP_IDS } from "./RouteCard";

const FIELDS: { key: ClientFieldKey; label: string; badge?: string; description: string }[] = [
  {
    key: "fan",
    label: "SocialCoach Fan ID",
    badge: "Exact match",
    description:
      "Your CRM's copy of our Fan ID, filled in by the API or webhooks. Blank for new clients, so keep Client ID on too.",
  },
  {
    key: "client_id",
    label: "Client ID",
    badge: "Recommended",
    description: "Your CRM's own contact ID. Stable, and keeps personal details out of the link.",
  },
  { key: "client_email", label: "Email", description: "Also finds clients you already have in SocialCoach." },
  { key: "lang", label: "Language", description: "Opens the form in the client's language when the form supports it." },
];

interface Props {
  state: BuilderState;
  onChange: (patch: Partial<BuilderState>) => void;
  setEnabled: FormRouteBuilder["setEnabled"];
}

export function FormCardClient({ state, onChange, setEnabled }: Props) {
  const passer = state.destination === "website" ? "page" : "CRM";
  return (
    <RouteCard
      step={3}
      id={STEP_IDS.client}
      title="What we know about the client"
      description={`Tick what your ${passer} can pass along. You map the fields under the route above. Names and phone numbers stay out of the link; the form asks for them.`}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
        {FIELDS.map(field => (
          <div key={field.key} className="flex items-start gap-2.5">
            <Checkbox
              className="mt-0.5"
              aria-label={field.label}
              checked={state.enabled[field.key]}
              onCheckedChange={checked => setEnabled(field.key, !!checked)}
            />
            <div className="flex flex-col gap-0.5">
              <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                {field.label}
                {field.badge && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                    {field.badge}
                  </Badge>
                )}
              </span>
              <span className="text-xs text-slate-500 leading-relaxed">{field.description}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-slate-100 pt-3">
        <span className="text-sm font-medium text-slate-700">Client type</span>
        <Segmented
          label="Client type"
          value={state.clientType}
          onChange={clientType => onChange({ clientType })}
          options={[
            { value: "customer", label: "Customer" },
            { value: "partner", label: "Partner" },
          ]}
        />
        <Helper>Partners get their own route.</Helper>
      </div>
      <Helper>
        We find the Fan by SocialCoach Fan ID, then Client ID, then email, and create a new Fan when nothing matches.
        Existing details are never overwritten. With none of these, responses come in without a Fan and the client types
        their details on the form.
      </Helper>
    </RouteCard>
  );
}
